import { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import WebsiteLogo from '../component/WebsiteLogo';
import ThemeToggle from '../component/ThemeToggle';
import LoginIllustration from '../component/LoginIllustration';
import { useWebsiteSettings } from '../hooks/useWebsiteSettings';
import {
    UNAUTH_ADMIN_REDIRECT,
    isAdminLoginPath,
} from '../helper/authPaths';

function Login() {
    const nav = useNavigate();
    const location = useLocation();
    const { settings: siteSettings } = useWebsiteSettings();
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(false);
    const allowedPath = isAdminLoginPath(location.pathname);

    useEffect(() => {
        if (!allowedPath) return;
        const saved = localStorage.getItem('loa_login_username');
        if (saved) {
            setUsername(saved);
            setRemember(true);
        }
    }, [allowedPath]);

    if (!allowedPath) {
        return <Navigate to={UNAUTH_ADMIN_REDIRECT} replace />;
    }

    const loginHandler = async (e) => {
        e.preventDefault();

        if (!username || !password) {
            swal('Error', 'Username dan password harus diisi!', 'error');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                'api/auth/login',
                { username, password },
                { withCredentials: true },
            );

            if (
                response.data.user?.role === 'admin' ||
                response.data.user?.role === 'operator'
            ) {
                if (remember) {
                    localStorage.setItem('loa_login_username', username);
                } else {
                    localStorage.removeItem('loa_login_username');
                }
                setLoading(false);
                nav('/admin');
            } else {
                setLoading(false);
                swal('Gagal', 'Akses ditolak!', 'error');
            }
        } catch (error) {
            setLoading(false);
            const errorMessage =
                error.response?.data?.message || 'Username atau password salah!';
            swal('Gagal', errorMessage, 'error');
        }
    };

    return (
        <div className="flex min-h-[100dvh] flex-col lg:flex-row">
            {/* Form */}
            <section className="flex min-h-[100dvh] flex-1 flex-col bg-surface-soft dark:bg-carbon lg:w-1/2 lg:bg-canvas">
                <div className="flex shrink-0 items-center justify-between border-b border-hairline bg-canvas px-4 py-4 dark:border-white/10 dark:bg-carbon lg:border-0 lg:bg-transparent lg:px-10 lg:py-8">
                    <WebsiteLogo
                        alt="Logo"
                        className="h-9 w-auto max-w-[160px] object-contain sm:max-w-[200px]"
                    />
                    <ThemeToggle />
                </div>

                <div className="flex flex-1 flex-col justify-center px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
                    <div className="card mx-auto w-full max-w-[400px] border border-hairline p-5 text-left dark:border-white/10 sm:p-6 lg:border-0 lg:bg-transparent lg:p-0 dark:lg:bg-transparent">
                        <div className="mb-5 h-0.5 w-10 bg-accent-brand lg:hidden" aria-hidden />
                        <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted lg:hidden">
                            {siteSettings.systemTitle}
                        </p>
                        <h1 className="mt-2 text-2xl font-semibold leading-tight text-ink dark:text-white sm:text-[28px] lg:mt-0 lg:text-[32px]">
                            Selamat datang kembali
                        </h1>
                        <p className="mt-2 text-sm text-muted">
                            Silakan masuk ke akun admin atau operator.
                        </p>

                        <form onSubmit={loginHandler} className="mt-6 space-y-4 sm:mt-8 sm:space-y-5">
                            <div>
                                <label
                                    htmlFor="username"
                                    className="mb-1.5 block text-sm font-medium text-ink dark:text-white/90"
                                >
                                    Username
                                </label>
                                <input
                                    id="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="login-field !bg-surface-soft dark:!bg-carbon lg:!bg-canvas dark:lg:!bg-tile-2"
                                    type="text"
                                    required
                                    autoComplete="username"
                                    placeholder="Masukkan username"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="password"
                                    className="mb-1.5 block text-sm font-medium text-ink dark:text-white/90"
                                >
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        className="login-field !bg-surface-soft !pr-11 dark:!bg-carbon lg:!bg-canvas dark:lg:!bg-tile-2"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Masukkan password"
                                        autoComplete="current-password"
                                        disabled={loading}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-soft transition-colors duration-tesla hover:text-primary dark:hover:text-white"
                                        aria-label={
                                            showPassword
                                                ? 'Sembunyikan password'
                                                : 'Tampilkan password'
                                        }
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <FaEyeSlash className="h-4 w-4" />
                                        ) : (
                                            <FaEye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                                <label className="flex cursor-pointer items-center gap-2 text-body dark:text-white/75">
                                    <input
                                        type="checkbox"
                                        checked={remember}
                                        onChange={(e) => setRemember(e.target.checked)}
                                        className="h-4 w-4 rounded border-border-strong text-primary focus:ring-0 dark:border-white/20"
                                    />
                                    Ingat username
                                </label>
                                <button
                                    type="button"
                                    onClick={() =>
                                        swal(
                                            'Info',
                                            'Hubungi administrator untuk reset password.',
                                            'info',
                                        )
                                    }
                                    className="self-start font-medium text-primary transition-colors duration-tesla hover:text-primary-active dark:text-primary-on-dark sm:self-auto"
                                >
                                    Lupa password?
                                </button>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary !min-h-11 w-full !rounded-xs disabled:cursor-not-allowed disabled:bg-primary-disabled"
                            >
                                {loading ? (
                                    <span className="inline-flex items-center gap-2">
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                        Memproses...
                                    </span>
                                ) : (
                                    'Masuk'
                                )}
                            </button>
                        </form>

                        <p className="mt-6 text-xs text-muted sm:mt-8 sm:text-sm">
                            Panel khusus admin & operator · {siteSettings.institutionName}
                        </p>
                    </div>
                </div>
            </section>

            {/* Ilustrasi — desktop saja */}
            <section className="relative hidden min-h-[100dvh] bg-primary lg:flex lg:w-1/2">
                <LoginIllustration
                    caption={`${siteSettings.systemTitle} — Admin Portal`}
                />
            </section>
        </div>
    );
}

export default Login;
